import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PatientDataEntry = () => {
  const { mrsId } = useParams();
  const { user } = useAuth();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [mrsDetails, setMrsDetails] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [patientsPerPage] = useState(10);
  const [templateData, setTemplateData] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [formInitialized, setFormInitialized] = useState(false); // Track if form has been initialized
  
  // Add states for patient deletion
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePatientInfo, setDeletePatientInfo] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Add states for patient view modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingPatient, setViewingPatient] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  // Add states for AI-powered data entry
  const [showAIEntry, setShowAIEntry] = useState(false);
  const [aiInputText, setAiInputText] = useState('');
  const [aiExtracting, setAiExtracting] = useState(false);
  const [aiExtractedData, setAiExtractedData] = useState(null);
  const [aiPreviewMode, setAiPreviewMode] = useState(false);
  const [entryMethod, setEntryMethod] = useState('manual'); // 'manual' or 'ai'

  // Add states for audio recording
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [transcribing, setTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingInterval, setRecordingInterval] = useState(null);
  const [audioSupported, setAudioSupported] = useState(true);

  const [formData, setFormData] = useState({
    patient_id: '',
    demographics: {},
    medical_history: {},
    therapy_details: {},
    outcomes: {}
  });

  useEffect(() => {
    fetchMRSDetails();
    fetchPatients();
  }, [mrsId, currentPage]);

  useEffect(() => {
    // Fetch template only once when component mounts
    fetchMRSTemplate();
  }, [mrsId]); // Only depend on mrsId, not currentPage

  useEffect(() => {
    // Check audio recording support
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setAudioSupported(false);
      console.warn('Audio recording not supported in this browser');
    }
  }, []);

  // Clean up intervals on unmount
  useEffect(() => {
    return () => {
      if (recordingInterval) {
        clearInterval(recordingInterval);
      }
    };
  }, [recordingInterval]);

  const fetchMRSDetails = async () => {
    try {
      const response = await axios.get(`${API}/mrs/${mrsId}?user_id=${user.id}`);
      setMrsDetails(response.data);
    } catch (error) {
      console.error('Error fetching MRS details:', error);
    }
  };

  const fetchMRSTemplate = async () => {
    setTemplateLoading(true);
    try {
      const response = await axios.get(`${API}/mrs/${mrsId}/template?user_id=${user.id}`);
      if (response.data && response.data.template_data) {
        setTemplateData(response.data);
        // Don't automatically initialize form - let user trigger it
      }
    } catch (error) {
      console.error('Error fetching MRS template:', error);
      setTemplateData(null);
    } finally {
      setTemplateLoading(false);
    }
  };

  const initializeFormFromTemplate = (templateData) => {
    if (!templateData || !templateData.sections) return null;
    
    const initialData = {
      patient_id: '',
      demographics: {},
      medical_history: {},
      therapy_details: {},
      outcomes: {}
    };

    // Initialize form fields based on template structure
    templateData.sections.forEach(section => {
      const sectionKey = section.title.toLowerCase().replace(/ /g, '_');
      let targetSection = 'demographics'; // default
      
      // Map section titles to form sections
      if (section.title.toLowerCase().includes('demographic') || 
          section.title.toLowerCase().includes('personal') ||
          section.title.toLowerCase().includes('basic')) {
        targetSection = 'demographics';
      } else if (section.title.toLowerCase().includes('history') || 
                 section.title.toLowerCase().includes('medical') ||
                 section.title.toLowerCase().includes('clinical')) {
        targetSection = 'medical_history';
      } else if (section.title.toLowerCase().includes('therapy') || 
                 section.title.toLowerCase().includes('treatment') ||
                 section.title.toLowerCase().includes('medication')) {
        targetSection = 'therapy_details';
      } else if (section.title.toLowerCase().includes('outcome') || 
                 section.title.toLowerCase().includes('result') ||
                 section.title.toLowerCase().includes('follow')) {
        targetSection = 'outcomes';
      }

      section.fields?.forEach(field => {
        // Skip any field that tries to conflict with patient_id
        if (field.id === 'patient_id') {
          console.warn(`⚠️ Skipping template field with conflicting ID: ${field.id}`);
          return;
        }
        
        // Initialize field with appropriate default value
        let defaultValue = '';
        if (field.type === 'checkbox') {
          defaultValue = false;
        } else if (field.type === 'number') {
          defaultValue = '';
        }
        
        initialData[targetSection][field.id] = defaultValue;
        console.log(`✅ Initialized ${targetSection}.${field.id} = ${defaultValue}`);
      });
    });

    return initialData;
  };

  const initializeForm = () => {
    if (templateData && templateData.template_data) {
      const initialData = initializeFormFromTemplate(templateData.template_data);
      if (initialData) {
        setFormData(initialData);
      }
    } else {
      // Fallback to default form structure
      const defaultData = {
        patient_id: '',
        demographics: {
          age: '',
          gender: '',
          ethnicity: '',
          weight: '',
          height: '',
          bmi: ''
        },
        medical_history: {
          diagnosis: '',
          comorbidities: '',
          allergies: '',
          previous_treatments: '',
          family_history: ''
        },
        therapy_details: {
          medication: '',
          dosage: '',
          duration: '',
          treatment_start: '',
          treatment_end: ''
        },
        outcomes: {
          primary_outcome: '',
          secondary_outcome: '',
          adverse_events: '',
          efficacy: '',
          safety: ''
        }
      };
      setFormData(defaultData);
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await axios.get(
        `${API}/mrs/${mrsId}/patients?user_id=${user.id}&skip=${currentPage * patientsPerPage}&limit=${patientsPerPage}`
      );
      setPatients(response.data);
    } catch (error) {
      console.error('Error fetching patients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const patientData = {
        ...formData,
        mrs_id: mrsId,
        patient_id: formData.patient_id || `PAT_${Date.now()}`
      };

      await axios.post(`${API}/mrs/${mrsId}/patients?user_id=${user.id}`, patientData);
      
      setShowAddModal(false);
      resetForm();
      setFormInitialized(false); // Reset form initialization flag
      fetchPatients();
      fetchMRSDetails(); // Refresh patient count
    } catch (error) {
      console.error('Error adding patient:', error);
    }
  };

  const openAddPatientModal = () => {
    setShowAddModal(true);
    
    // Only initialize form if it hasn't been initialized yet for this modal session
    if (!formInitialized) {
      setTimeout(() => {
        initializeForm();
        setFormInitialized(true);
      }, 200);
    }
  };

  const resetForm = () => {
    initializeForm();
  };

  // Patient deletion functions
  const handleDeletePatient = async (patient) => {
    try {
      const response = await axios.get(`${API}/mrs/${mrsId}/patients/${patient.id}/deletion-info?user_id=${user.id}`);
      setDeletePatientInfo(response.data);
      setShowDeleteConfirm(true);
    } catch (error) {
      console.error('Error fetching patient deletion info:', error);
      // Fallback to basic confirmation
      setDeletePatientInfo({
        patient_id: patient.id,
        patient_identifier: patient.patient_id || 'Unknown',
        mrs_name: mrsDetails?.name || 'Unknown MRS'
      });
      setShowDeleteConfirm(true);
    }
  };

  // Patient view functions
  const handleViewPatient = async (patient) => {
    setViewLoading(true);
    try {
      const response = await axios.get(`${API}/mrs/${mrsId}/patients/${patient.id}?user_id=${user.id}`);
      setViewingPatient(response.data);
      setShowViewModal(true);
    } catch (error) {
      console.error('Error fetching patient details:', error);
      alert('Error loading patient details: ' + (error.response?.data?.detail || error.message));
    } finally {
      setViewLoading(false);
    }
  };

  // AI-powered data entry functions
  const handleAIExtraction = async (createPatient = false) => {
    if (!aiInputText.trim()) {
      alert('Please enter patient information to extract');
      return;
    }

    setAiExtracting(true);
    try {
      const response = await axios.post(`${API}/mrs/${mrsId}/patients/ai-extract`, {
        free_text: aiInputText,
        create_patient: createPatient
      }, {
        params: { user_id: user.id }
      });

      if (response.data.success) {
        setAiExtractedData(response.data.extracted_data);
        
        if (createPatient) {
          // Patient was created successfully
          alert(response.data.message);
          setShowAIEntry(false);
          setAiInputText('');
          setAiExtractedData(null);
          setAiPreviewMode(false);
          fetchPatients(); // Refresh patient list
        } else {
          // Show preview mode
          setAiPreviewMode(true);
          alert('Data extracted successfully! Please review the extracted information and confirm to create the patient record.');
        }
      }
    } catch (error) {
      console.error('Error with AI extraction:', error);
      alert('AI extraction failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAiExtracting(false);
    }
  };

  const handleAIPreviewConfirm = () => {
    handleAIExtraction(true); // Create patient with extracted data
  };

  const handleAIPreviewEdit = () => {
    // Convert extracted data to form data and switch to manual mode
    if (aiExtractedData) {
      setFormData({
        patient_id: aiExtractedData.patient_id || '',
        demographics: aiExtractedData.demographics || {},
        medical_history: aiExtractedData.medical_history || {},
        therapy_details: aiExtractedData.therapy_details || {},
        outcomes: aiExtractedData.outcomes || {}
      });
      setEntryMethod('manual');
      setAiPreviewMode(false);
      setShowAIEntry(false);
    }
  };

  const resetAIEntry = () => {
    setAiInputText('');
    setAiExtractedData(null);
    setAiPreviewMode(false);
    setShowAIEntry(false);
    stopRecording(); // Also stop any ongoing recording
  };

  // Audio recording functions
  const startRecording = async () => {
    if (!audioSupported) {
      alert('Audio recording is not supported in your browser. Please use a modern browser like Chrome, Firefox, or Safari.');
      return;
    }

    try {
      // Try to use browser's Speech Recognition API first (free and immediate)
      if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
        startSpeechRecognition();
      } else {
        // Fallback to audio recording + server-side transcription
        startAudioRecording();
      }
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Could not start recording. Please try again.');
    }
  };

  const startSpeechRecognition = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    
    let finalTranscript = '';
    let interimTranscript = '';
    
    recognition.onstart = () => {
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start recording timer
      const interval = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      setRecordingInterval(interval);
    };
    
    recognition.onresult = (event) => {
      finalTranscript = '';
      interimTranscript = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }
      
      // Update the text area in real-time
      const currentText = aiInputText + finalTranscript + interimTranscript;
      if (currentText !== aiInputText) {
        setAiInputText(aiInputText + finalTranscript);
      }
    };
    
    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsRecording(false);
      if (recordingInterval) {
        clearInterval(recordingInterval);
        setRecordingInterval(null);
      }
      alert('Speech recognition error: ' + event.error + '. You can still type your description manually.');
    };
    
    recognition.onend = () => {
      setIsRecording(false);
      setRecordingTime(0);
      if (recordingInterval) {
        clearInterval(recordingInterval);
        setRecordingInterval(null);
      }
    };
    
    recognition.start();
    setMediaRecorder(recognition); // Store for stopping later
  };

  const startAudioRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ 
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        sampleRate: 44100
      } 
    });
    
    const recorder = new MediaRecorder(stream, {
      mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
    });
    
    const chunks = [];
    
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };
    
    recorder.onstop = () => {
      const audioBlob = new Blob(chunks, { type: recorder.mimeType });
      handleAudioTranscription(audioBlob);
      stream.getTracks().forEach(track => track.stop()); // Stop microphone access
    };
    
    recorder.start();
    setMediaRecorder(recorder);
    setAudioChunks(chunks);
    setIsRecording(true);
    setRecordingTime(0);
    
    // Start recording timer
    const interval = setInterval(() => {
      setRecordingTime(prev => prev + 1);
    }, 1000);
    setRecordingInterval(interval);
  };

  const stopRecording = () => {
    if (mediaRecorder) {
      if (mediaRecorder.stop) {
        // MediaRecorder
        if (mediaRecorder.state !== 'inactive') {
          mediaRecorder.stop();
        }
      } else if (mediaRecorder.abort) {
        // SpeechRecognition
        mediaRecorder.abort();
      }
    }
    setIsRecording(false);
    setRecordingTime(0);
    
    if (recordingInterval) {
      clearInterval(recordingInterval);
      setRecordingInterval(null);
    }
  };

  const handleAudioTranscription = async (audioBlob) => {
    setTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');
      
      const response = await axios.post(`${API}/mrs/${mrsId}/patients/audio-transcribe`, formData, {
        params: { user_id: user.id },
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        const transcribedText = response.data.transcribed_text;
        setAiInputText(transcribedText);
        alert(`Audio transcribed successfully! ${transcribedText.length} characters transcribed. You can now review and edit the text before AI extraction.`);
      }
    } catch (error) {
      console.error('Error transcribing audio:', error);
      
      // Extract error message from response
      const errorMessage = error.response?.data?.detail || error.message;
      
      // Check if it's the specific audio transcription unavailable error
      if (errorMessage.includes('not available') || errorMessage.includes('temporarily disabled') || errorMessage.includes('manually')) {
        // Show user-friendly message for audio transcription unavailability
        alert(
          `Audio Transcription Notice:\n\n${errorMessage}\n\n` +
          `Tip: You can still speak your description and then type the key points in the text area below. ` +
          `The AI will extract structured patient data from your written text.`
        );
      } else {
        // Show generic error for other issues
        alert(`Audio transcription encountered an error: ${errorMessage}\n\nPlease type your patient description manually in the text area.`);
      }
    } finally {
      setTranscribing(false);
    }
  };

  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Generate dynamic example prompt based on template data
  const generateDynamicExample = () => {
    if (!templateData || !templateData.template_data || !templateData.template_data.sections) {
      return "Example: 45-year-old male patient with diabetes, taking metformin 500mg twice daily, HbA1c improved from 8.2% to 6.8% after 3 months of treatment...";
    }

    const sections = templateData.template_data.sections;
    const specialty = templateData.specialty || "General";
    let exampleParts = [];
    
    // Helper function to get sample values for different field types
    const getSampleValue = (field, sectionTitle) => {
      const fieldId = field.id.toLowerCase();
      const fieldLabel = field.label.toLowerCase();
      const fieldType = field.type;
      
      // Age samples
      if (fieldId.includes('age') || fieldLabel.includes('age')) {
        return Math.floor(Math.random() * 40) + 25; // Random age 25-65
      }
      
      // Gender samples
      if (fieldId.includes('gender') || fieldLabel.includes('gender') || fieldId.includes('sex')) {
        return Math.random() > 0.5 ? 'male' : 'female';
      }
      
      // Weight samples
      if (fieldId.includes('weight') || fieldLabel.includes('weight')) {
        return Math.floor(Math.random() * 50) + 130 + ' pounds'; // 130-180 pounds
      }
      
      // Height samples
      if (fieldId.includes('height') || fieldLabel.includes('height')) {
        const feet = Math.floor(Math.random() * 2) + 5; // 5-6 feet
        const inches = Math.floor(Math.random() * 12); // 0-11 inches
        return `${feet} feet ${inches} inches`;
      }
      
      // Diagnosis samples by specialty
      if (fieldId.includes('diagnosis') || fieldLabel.includes('diagnosis') || fieldId.includes('condition')) {
        const diagnosisSamples = {
          'Cardiology': ['coronary artery disease', 'hypertension', 'heart failure', 'atrial fibrillation'],
          'Endocrinology': ['Type 2 diabetes mellitus', 'hypothyroidism', 'diabetic neuropathy'],
          'Pulmonology': ['chronic obstructive pulmonary disease', 'asthma', 'pneumonia'],
          'Neurology': ['migraine headaches', 'epilepsy', 'Parkinson disease'],
          'Oncology': ['breast cancer', 'lung adenocarcinoma', 'colorectal cancer'],
          'Orthopedics': ['osteoarthritis', 'rheumatoid arthritis', 'lumbar disc herniation']
        };
        const samples = diagnosisSamples[specialty] || ['chronic condition', 'medical condition'];
        return samples[Math.floor(Math.random() * samples.length)];
      }
      
      // Medication samples by specialty
      if (fieldId.includes('medication') || fieldId.includes('drug') || fieldId.includes('therapy') || fieldLabel.includes('medication')) {
        const medicationSamples = {
          'Cardiology': ['Metoprolol 50mg twice daily', 'Lisinopril 10mg once daily', 'Atorvastatin 20mg at bedtime'],
          'Endocrinology': ['Metformin 1000mg twice daily', 'Insulin glargine 20 units at bedtime', 'Levothyroxine 75 mcg daily'],
          'Pulmonology': ['Albuterol inhaler as needed', 'Fluticasone 100 mcg twice daily', 'Prednisone 20mg daily'],
          'Neurology': ['Sumatriptan 50mg as needed for headache', 'Levetiracetam 500mg twice daily'],
          'Oncology': ['Tamoxifen 20mg daily', 'Ondansetron 8mg before chemotherapy'],
          'Orthopedics': ['Ibuprofen 600mg three times daily', 'Acetaminophen 1000mg twice daily']
        };
        const samples = medicationSamples[specialty] || ['current medication'];
        return samples[Math.floor(Math.random() * samples.length)];
      }
      
      // Outcome samples
      if (fieldId.includes('outcome') || fieldId.includes('result') || fieldId.includes('response') || fieldLabel.includes('outcome')) {
        const outcomes = ['improved significantly', 'stable condition', 'good response to treatment', 'symptoms well controlled'];
        return outcomes[Math.floor(Math.random() * outcomes.length)];
      }
      
      // Lab values and measurements
      if (fieldId.includes('hba1c') || fieldLabel.includes('hba1c')) {
        return `HbA1c improved from ${(Math.random() * 2 + 7).toFixed(1)}% to ${(Math.random() * 1.5 + 6).toFixed(1)}%`;
      }
      
      if (fieldId.includes('blood_pressure') || fieldId.includes('bp') || fieldLabel.includes('blood pressure')) {
        return `blood pressure ${Math.floor(Math.random() * 40 + 120)}/${Math.floor(Math.random() * 20 + 70)}`;
      }
      
      // Adverse events
      if (fieldId.includes('adverse') || fieldId.includes('side_effect') || fieldLabel.includes('adverse')) {
        return Math.random() > 0.7 ? 'mild nausea initially' : 'no adverse events reported';
      }
      
      // Compliance
      if (fieldId.includes('compliance') || fieldId.includes('adherence') || fieldLabel.includes('compliance')) {
        return 'excellent medication compliance';
      }
      
      // Family history
      if (fieldId.includes('family') || fieldId.includes('history') || fieldLabel.includes('family')) {
        const familyHistory = ['diabetes', 'heart disease', 'cancer', 'hypertension'];
        return `family history of ${familyHistory[Math.floor(Math.random() * familyHistory.length)]}`;
      }
      
      // Default return based on field type
      if (fieldType === 'number') {
        return 'appropriate numeric value';
      } else if (fieldType === 'date') {
        return 'relevant date';
      } else if (field.options && field.options.length > 0) {
        return field.options[Math.floor(Math.random() * field.options.length)];
      }
      
      return `relevant ${field.label.toLowerCase()}`;
    };

    // Build example parts for each section
    sections.forEach(section => {
      if (!section.fields || section.fields.length === 0) return;
      
      const sectionTitle = section.title;
      const sectionParts = [];
      
      // Get up to 3 most important fields from each section
      const importantFields = section.fields
        .filter(field => field.required || 
                field.id.includes('age') || field.id.includes('gender') || 
                field.id.includes('diagnosis') || field.id.includes('medication') || 
                field.id.includes('outcome'))
        .slice(0, 3);
      
      // If no important fields, take first 2 fields
      const fieldsToUse = importantFields.length > 0 ? importantFields : section.fields.slice(0, 2);
      
      fieldsToUse.forEach(field => {
        const sampleValue = getSampleValue(field, sectionTitle);
        if (sampleValue && typeof sampleValue !== 'object') {
          sectionParts.push(sampleValue);
        }
      });
      
      if (sectionParts.length > 0) {
        exampleParts = exampleParts.concat(sectionParts);
      }
    });

    // Construct final example
    if (exampleParts.length === 0) {
      return "Example: Describe the patient's demographics, medical history, treatments, and clinical outcomes in natural language...";
    }

    // Get a sample age and gender for consistent narrative
    const sampleAge = Math.floor(Math.random() * 40) + 25;
    const sampleGender = Math.random() > 0.5 ? 'male' : 'female';
    
    // Create a cohesive narrative
    let example = `Example: ${sampleAge}-year-old ${sampleGender} patient with `;
    
    // Add diagnosis
    const diagnosisTerms = exampleParts.filter(part => 
      typeof part === 'string' && (
        part.includes('diabetes') || part.includes('hypertension') || 
        part.includes('disease') || part.includes('cancer') || 
        part.includes('asthma') || part.includes('arthritis')
      )
    );
    
    if (diagnosisTerms.length > 0) {
      example += diagnosisTerms[0] + ', ';
    }
    
    // Add medications
    const medicationTerms = exampleParts.filter(part => 
      typeof part === 'string' && (
        part.includes('mg') || part.includes('daily') || part.includes('twice') || 
        part.includes('medication') || part.includes('insulin') || part.includes('inhaler')
      )
    );
    
    if (medicationTerms.length > 0) {
      example += 'taking ' + medicationTerms[0];
      if (medicationTerms.length > 1) {
        example += ' and ' + medicationTerms[1];
      }
      example += '. ';
    }
    
    // Add outcomes or improvements
    const outcomeTerms = exampleParts.filter(part => 
      typeof part === 'string' && (
        part.includes('improved') || part.includes('stable') || 
        part.includes('response') || part.includes('HbA1c') || 
        part.includes('blood pressure')
      )
    );
    
    if (outcomeTerms.length > 0) {
      example += outcomeTerms[0] + '. ';
    }
    
    // Add compliance and adverse events
    const complianceTerms = exampleParts.filter(part => 
      typeof part === 'string' && (
        part.includes('compliance') || part.includes('adverse') || 
        part.includes('side effect') || part.includes('no adverse')
      )
    );
    
    if (complianceTerms.length > 0) {
      example += 'Patient reports ' + complianceTerms[0] + '. ';
    }
    
    // Add any remaining relevant information
    const otherTerms = exampleParts.filter(part => 
      typeof part === 'string' && 
      !part.includes('male') && !part.includes('female') && 
      !medicationTerms.includes(part) && !diagnosisTerms.includes(part) && 
      !outcomeTerms.includes(part) && !complianceTerms.includes(part)
    );
    
    if (otherTerms.length > 0) {
      example += otherTerms.slice(0, 2).join(', ') + '.';
    }
    
    // Ensure example isn't too long
    if (example.length > 300) {
      example = example.substring(0, 297) + '...';
    }
    
    return example;
  };

  const executePatientDeletion = async () => {
    if (!deletePatientInfo) return;
    
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/mrs/${mrsId}/patients/${deletePatientInfo.patient_id}?user_id=${user.id}`);
      
      // Refresh patient list and MRS details
      fetchPatients();
      fetchMRSDetails();
      
      // Close confirmation dialog
      setShowDeleteConfirm(false);
      setDeletePatientInfo(null);
      
      console.log('Patient record deleted successfully');
    } catch (error) {
      console.error('Error deleting patient:', error);
      alert('Error deleting patient: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeleteLoading(false);
    }
  };

  const canManagePatient = () => {
    return mrsDetails?.owner_id === user.id || ['admin', 'hod'].includes(user.role);
  };
  
  const hasPatientIdInTemplate = () => {
    if (!templateData || !templateData.template_data || !templateData.template_data.sections) {
      return false;
    }
    
    // Check if any template section has a field with ID 'patient_id'
    return templateData.template_data.sections.some(section => 
      section.fields?.some(field => field.id === 'patient_id')
    );
  };

  const handleInputChange = (section, field, value) => {
    console.log(`🔍 Input change: section="${section}", field="${field}", value="${value}"`);
    
    if (section === 'patient_id') {
      setFormData(prev => ({ ...prev, patient_id: value }));
    } else {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    }
  };

  const renderTemplateField = (field, sectionKey) => {
    // Ensure we get the correct field value for this specific section and field
    const fieldValue = formData[sectionKey]?.[field.id] || '';
    
    // Debug logging to identify issues
    console.log(`🎯 Rendering field: ${sectionKey}.${field.id} = "${fieldValue}"`);
    
    switch (field.type) {
      case 'text':
        return (
          <div key={`${sectionKey}-${field.id}`}>
            <label className="form-label">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="text"
              className="form-input"
              value={fieldValue}
              onChange={(e) => handleInputChange(sectionKey, field.id, e.target.value)}
              placeholder={field.placeholder}
              required={field.required}
            />
          </div>
        );
      
      case 'number':
        return (
          <div key={`${sectionKey}-${field.id}`}>
            <label className="form-label">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="number"
              className="form-input"
              value={fieldValue}
              onChange={(e) => handleInputChange(sectionKey, field.id, e.target.value)}
              placeholder={field.placeholder}
              min={field.min}
              max={field.max}
              step={field.step}
              required={field.required}
            />
          </div>
        );
      
      case 'select':
        return (
          <div key={`${sectionKey}-${field.id}`}>
            <label className="form-label">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <select
              className="form-input"
              value={fieldValue}
              onChange={(e) => handleInputChange(sectionKey, field.id, e.target.value)}
              required={field.required}
            >
              <option value="">Select {field.label.toLowerCase()}</option>
              {field.options?.map((option, index) => (
                <option key={index} value={option}>{option}</option>
              ))}
            </select>
          </div>
        );
      
      case 'textarea':
        return (
          <div key={`${sectionKey}-${field.id}`}>
            <label className="form-label">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <textarea
              className="form-input"
              rows={3}
              value={fieldValue}
              onChange={(e) => handleInputChange(sectionKey, field.id, e.target.value)}
              placeholder={field.placeholder}
              required={field.required}
            />
          </div>
        );
      
      case 'date':
        return (
          <div key={`${sectionKey}-${field.id}`}>
            <label className="form-label">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="date"
              className="form-input"
              value={fieldValue}
              onChange={(e) => handleInputChange(sectionKey, field.id, e.target.value)}
              required={field.required}
            />
          </div>
        );
      
      case 'checkbox':
        return (
          <div key={`${sectionKey}-${field.id}`} className="flex items-center">
            <input
              type="checkbox"
              id={`${sectionKey}-${field.id}`}
              className="h-4 w-4 text-sky-600 border-gray-300 rounded focus:ring-sky-500"
              checked={fieldValue === true}
              onChange={(e) => handleInputChange(sectionKey, field.id, e.target.checked)}
            />
            <label htmlFor={`${sectionKey}-${field.id}`} className="ml-2 text-sm text-gray-700">
              {field.label}
            </label>
          </div>
        );
      
      default:
        return (
          <div key={`${sectionKey}-${field.id}`}>
            <label className="form-label">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="text"
              className="form-input"
              value={fieldValue}
              onChange={(e) => handleInputChange(sectionKey, field.id, e.target.value)}
              placeholder={field.placeholder}
              required={field.required}
            />
          </div>
        );
    }
  };

  const renderTemplateForm = () => {
    if (!templateData || !templateData.template_data || templateLoading) {
      return (
        <div className="text-center py-8">
          {templateLoading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-sky-600 mr-2"></div>
              <span className="text-gray-600">Loading form template...</span>
            </div>
          ) : (
            <div className="text-gray-500">
              <p>No form template found for this MRS.</p>
              <p className="text-sm mt-1">Using default form structure.</p>
            </div>
          )}
        </div>
      );
    }

    const { sections } = templateData.template_data;
    
    return (
      <div className="space-y-6">
        {sections.map((section, sectionIndex) => {
          // Map section titles to form section keys
          let sectionKey = 'demographics'; // default
          
          if (section.title.toLowerCase().includes('demographic') || 
              section.title.toLowerCase().includes('personal') ||
              section.title.toLowerCase().includes('basic')) {
            sectionKey = 'demographics';
          } else if (section.title.toLowerCase().includes('history') || 
                     section.title.toLowerCase().includes('medical') ||
                     section.title.toLowerCase().includes('clinical')) {
            sectionKey = 'medical_history';
          } else if (section.title.toLowerCase().includes('therapy') || 
                     section.title.toLowerCase().includes('treatment') ||
                     section.title.toLowerCase().includes('medication')) {
            sectionKey = 'therapy_details';
          } else if (section.title.toLowerCase().includes('outcome') || 
                     section.title.toLowerCase().includes('result') ||
                     section.title.toLowerCase().includes('follow')) {
            sectionKey = 'outcomes';
          }

          return (
            <div key={sectionIndex} className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                {section.title}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {section.fields?.map(field => renderTemplateField(field, sectionKey))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const getSpecialtyColor = (specialty) => {
    const colors = {
      'Cardiology': 'bg-red-100 text-red-800',
      'Endocrinology': 'bg-green-100 text-green-800',
      'Neurology': 'bg-sky-100 text-sky-800',
      'Oncology': 'bg-purple-100 text-purple-800',
      'Psychiatry': 'bg-indigo-100 text-indigo-800',
      'Orthopedics': 'bg-orange-100 text-orange-800',
      'Pediatrics': 'bg-pink-100 text-pink-800',
      'Geriatrics': 'bg-yellow-100 text-yellow-800'
    };
    return colors[specialty] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/mrs"
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Patient Records</h1>
            {mrsDetails && (
              <div className="flex items-center space-x-2 mt-2">
                <span className={`specialty-badge ${getSpecialtyColor(mrsDetails.specialty)}`}>
                  {mrsDetails.specialty}
                </span>
                <span className="text-gray-600">{mrsDetails.name}</span>
                <span className="text-gray-400">•</span>
                <span className="text-gray-600">{mrsDetails.patient_count} patients</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex space-x-3">
          <Link
            to={`/mrs/${mrsId}/upload`}
            className="btn-secondary flex items-center space-x-2"
          >
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            <span>Upload Data</span>
          </Link>
          
          {/* Add Patient Options */}
          <div className="relative group">
            <button className="btn-primary flex items-center space-x-2">
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              <span>Add Patient</span>
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
            
            {/* Dropdown Menu */}
            <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-md shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
              <div className="py-2" role="menu">
                <button
                  onClick={() => {
                    setEntryMethod('ai');
                    setShowAIEntry(true);
                  }}
                  className="flex items-start w-full px-4 py-3 text-left text-sm hover:bg-sky-50 border-b border-gray-100"
                >
                  <div className="flex-shrink-0 mr-3 mt-0.5">
                    <svg className="h-5 w-5 text-sky-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>
                  <div>
                    <div className="font-medium text-sky-900">🤖 AI-Powered Entry</div>
                    <div className="text-xs text-sky-700 mt-1">Enter free-text patient description, AI extracts structured data</div>
                  </div>
                </button>
                
                <button
                  onClick={openAddPatientModal}
                  className="flex items-start w-full px-4 py-3 text-left text-sm hover:bg-gray-50"
                >
                  <div className="flex-shrink-0 mr-3 mt-0.5">
                    <svg className="h-5 w-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" clipRule="evenodd" />
                      <path fillRule="evenodd" d="M3 8a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">📝 Manual Entry</div>
                    <div className="text-xs text-gray-700 mt-1">Fill out form fields manually</div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Patients List */}
      {patients.length > 0 ? (
        <div className="medical-card">
          <div className="medical-card-header">
            <h2 className="text-lg font-semibold text-gray-900">Patient Records</h2>
          </div>
          <div className="medical-card-content p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Patient ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Demographics
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Diagnosis
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Treatment
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Outcomes
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {patients.map((patient) => (
                    <tr key={patient.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {patient.patient_id}
                        </div>
                        <div className="text-sm text-gray-500">
                          {new Date(patient.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {patient.demographics.age && `Age: ${patient.demographics.age}`}
                          {patient.demographics.age && patient.demographics.gender && ', '}
                          {patient.demographics.gender}
                        </div>
                        {patient.demographics.ethnicity && (
                          <div className="text-sm text-gray-500">
                            {patient.demographics.ethnicity}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {patient.medical_history.diagnosis || 'Not specified'}
                        </div>
                        {patient.medical_history.comorbidities && (
                          <div className="text-sm text-gray-500">
                            Comorbidities: {patient.medical_history.comorbidities}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {patient.therapy_details.medication || 'Not specified'}
                        </div>
                        {patient.therapy_details.dosage && (
                          <div className="text-sm text-gray-500">
                            {patient.therapy_details.dosage}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {patient.outcomes.primary_outcome || 'Pending'}
                        </div>
                        {patient.outcomes.adverse_events && (
                          <div className="text-sm text-red-600">
                            AE: {patient.outcomes.adverse_events}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {/* View Button - Always visible */}
                          <button
                            onClick={() => handleViewPatient(patient)}
                            disabled={viewLoading && viewingPatient?.id === patient.id}
                            className="text-sky-600 hover:text-sky-800 px-2 py-1 text-sm font-medium"
                          >
                            {viewLoading && viewingPatient?.id === patient.id ? (
                              <div className="flex items-center">
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-sky-600 mr-1"></div>
                                Loading...
                              </div>
                            ) : (
                              'View'
                            )}
                          </button>
                          
                          {/* Management Actions - Only for authorized users */}
                          {canManagePatient() && (
                            <div className="relative group">
                              <button className="text-gray-400 hover:text-gray-600 p-1">
                                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
                                </svg>
                              </button>
                              
                              {/* Dropdown Menu */}
                              <div className="absolute right-0 top-full mt-1 w-32 bg-white rounded-md shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                                <div className="py-1" role="menu">
                                  <button
                                    onClick={() => handleDeletePatient(patient)}
                                    className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                                  >
                                    <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" clipRule="evenodd" />
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                    Delete
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {currentPage * patientsPerPage + 1} to {Math.min((currentPage + 1) * patientsPerPage, patients.length)} of {patients.length} patients
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                  disabled={currentPage === 0}
                  className="btn-secondary text-sm py-1 px-3 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={patients.length < patientsPerPage}
                  className="btn-secondary text-sm py-1 px-3 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <svg className="h-24 w-24 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
          </svg>
          <h3 className="text-xl font-medium text-gray-900 mb-2">No Patient Records</h3>
          <p className="text-gray-600 mb-6">Add patient data manually or upload from Excel/CSV files.</p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={openAddPatientModal}
              className="btn-primary"
            >
              Add Patient Manually
            </button>
            <Link
              to={`/mrs/${mrsId}/upload`}
              className="btn-secondary"
            >
              Upload Data File
            </Link>
          </div>
        </div>
      )}

      {/* Add Patient Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Add New Patient</h2>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setFormInitialized(false); // Reset form initialization flag
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Patient ID - Only show if not already present in template */}
              {!templateData || !hasPatientIdInTemplate() ? (
                <div>
                  <label className="form-label">Patient ID (Optional)</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.patient_id}
                    onChange={(e) => handleInputChange('patient_id', '', e.target.value)}
                    placeholder="Auto-generated if left blank"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Leave blank to auto-generate a unique Patient ID
                  </p>
                </div>
              ) : null}

              {/* Template-based form or default form */}
              {templateData && templateData.template_data ? (
                <div className="space-y-1 mb-4">
                  <div className="flex items-center space-x-2 text-sm text-sky-600 bg-sky-50 p-2 rounded">
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <span>Using form template: <strong>{templateData.name}</strong></span>
                  </div>
                </div>
              ) : null}
              
              {templateData && templateData.template_data ? (
                renderTemplateForm()
              ) : (
                <>
                  {/* Default Demographics Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">Demographics</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="form-label">Age</label>
                        <input
                          type="number"
                          className="form-input"
                          value={formData.demographics.age}
                          onChange={(e) => handleInputChange('demographics', 'age', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="form-label">Gender</label>
                        <select
                          className="form-input"
                          value={formData.demographics.gender}
                          onChange={(e) => handleInputChange('demographics', 'gender', e.target.value)}
                        >
                          <option value="">Select gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="form-label">Ethnicity</label>
                        <input
                          type="text"
                          className="form-input"
                          value={formData.demographics.ethnicity}
                          onChange={(e) => handleInputChange('demographics', 'ethnicity', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="form-label">Weight (kg)</label>
                        <input
                          type="number"
                          step="0.1"
                          className="form-input"
                          value={formData.demographics.weight}
                          onChange={(e) => handleInputChange('demographics', 'weight', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="form-label">Height (cm)</label>
                        <input
                          type="number"
                          className="form-input"
                          value={formData.demographics.height}
                          onChange={(e) => handleInputChange('demographics', 'height', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="form-label">BMI</label>
                        <input
                          type="number"
                          step="0.1"
                          className="form-input"
                          value={formData.demographics.bmi}
                          onChange={(e) => handleInputChange('demographics', 'bmi', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Default Medical History Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">Medical History</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="form-label">Primary Diagnosis</label>
                        <input
                          type="text"
                          className="form-input"
                          value={formData.medical_history.diagnosis}
                          onChange={(e) => handleInputChange('medical_history', 'diagnosis', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="form-label">Comorbidities</label>
                        <input
                          type="text"
                          className="form-input"
                          value={formData.medical_history.comorbidities}
                          onChange={(e) => handleInputChange('medical_history', 'comorbidities', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="form-label">Allergies</label>
                        <input
                          type="text"
                          className="form-input"
                          value={formData.medical_history.allergies}
                          onChange={(e) => handleInputChange('medical_history', 'allergies', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="form-label">Previous Treatments</label>
                        <input
                          type="text"
                          className="form-input"
                          value={formData.medical_history.previous_treatments}
                          onChange={(e) => handleInputChange('medical_history', 'previous_treatments', e.target.value)}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="form-label">Family History</label>
                        <textarea
                          rows={2}
                          className="form-input"
                          value={formData.medical_history.family_history}
                          onChange={(e) => handleInputChange('medical_history', 'family_history', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Default Therapy Details Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">Therapy Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="form-label">Medication</label>
                        <input
                          type="text"
                          className="form-input"
                          value={formData.therapy_details.medication}
                          onChange={(e) => handleInputChange('therapy_details', 'medication', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="form-label">Dosage</label>
                        <input
                          type="text"
                          className="form-input"
                          value={formData.therapy_details.dosage}
                          onChange={(e) => handleInputChange('therapy_details', 'dosage', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="form-label">Duration</label>
                        <input
                          type="text"
                          className="form-input"
                          value={formData.therapy_details.duration}
                          onChange={(e) => handleInputChange('therapy_details', 'duration', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="form-label">Treatment Start Date</label>
                        <input
                          type="date"
                          className="form-input"
                          value={formData.therapy_details.treatment_start}
                          onChange={(e) => handleInputChange('therapy_details', 'treatment_start', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Default Outcomes Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">Outcomes</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="form-label">Primary Outcome</label>
                        <input
                          type="text"
                          className="form-input"
                          value={formData.outcomes.primary_outcome}
                          onChange={(e) => handleInputChange('outcomes', 'primary_outcome', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="form-label">Secondary Outcome</label>
                        <input
                          type="text"
                          className="form-input"
                          value={formData.outcomes.secondary_outcome}
                          onChange={(e) => handleInputChange('outcomes', 'secondary_outcome', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="form-label">Adverse Events</label>
                        <input
                          type="text"
                          className="form-input"
                          value={formData.outcomes.adverse_events}
                          onChange={(e) => handleInputChange('outcomes', 'adverse_events', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="form-label">Efficacy Rating</label>
                        <select
                          className="form-input"
                          value={formData.outcomes.efficacy}
                          onChange={(e) => handleInputChange('outcomes', 'efficacy', e.target.value)}
                        >
                          <option value="">Select efficacy</option>
                          <option value="Excellent">Excellent</option>
                          <option value="Good">Good</option>
                          <option value="Fair">Fair</option>
                          <option value="Poor">Poor</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </>
              )}
              
              <div className="flex space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setFormInitialized(false); // Reset form initialization flag
                  }}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="flex-1 btn-primary">
                  Add Patient
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Patient Deletion Confirmation Dialog */}
      {showDeleteConfirm && deletePatientInfo && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Delete Patient Record</h2>
            </div>
            
            <div className="p-6">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                
                <div className="flex-1">
                  <p className="text-sm text-gray-700 mb-3">
                    Are you sure you want to permanently delete this patient record? This action cannot be undone.
                  </p>
                  
                  <div className="p-3 bg-gray-50 rounded">
                    <p className="text-sm font-medium text-gray-900">
                      Patient: {deletePatientInfo.patient_identifier}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      MRS: {deletePatientInfo.mrs_name}
                    </p>
                    {deletePatientInfo.demographics && (
                      <div className="text-xs text-gray-600 mt-1">
                        {deletePatientInfo.demographics.age && `Age: ${deletePatientInfo.demographics.age}`}
                        {deletePatientInfo.demographics.age && deletePatientInfo.demographics.gender && ', '}
                        {deletePatientInfo.demographics.gender}
                      </div>
                    )}
                    {deletePatientInfo.created_at && (
                      <p className="text-xs text-gray-500 mt-1">
                        Created: {new Date(deletePatientInfo.created_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletePatientInfo(null);
                }}
                disabled={deleteLoading}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={executePatientDeletion}
                disabled={deleteLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Deleting...
                  </div>
                ) : (
                  'Delete Patient'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Patient View Modal */}
      {showViewModal && viewingPatient && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Patient Details</h2>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setViewingPatient(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Patient Header Info */}
              <div className="bg-sky-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-sky-900">
                      Patient ID: {viewingPatient.patient_id}
                    </h3>
                    <p className="text-sm text-sky-700">
                      Added on {new Date(viewingPatient.created_at).toLocaleDateString()} at {new Date(viewingPatient.created_at).toLocaleTimeString()}
                    </p>
                    {viewingPatient.updated_at && viewingPatient.updated_at !== viewingPatient.created_at && (
                      <p className="text-sm text-sky-700">
                        Last updated: {new Date(viewingPatient.updated_at).toLocaleDateString()} at {new Date(viewingPatient.updated_at).toLocaleTimeString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Demographics Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">Demographics</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(viewingPatient.demographics || {}).map(([key, value]) => (
                    value && (
                      <div key={key} className="bg-gray-50 p-3 rounded">
                        <label className="text-sm font-medium text-gray-700 capitalize">
                          {key.replace(/_/g, ' ')}
                        </label>
                        <p className="text-sm text-gray-900 mt-1">
                          {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
                        </p>
                      </div>
                    )
                  ))}
                  {(!viewingPatient.demographics || Object.keys(viewingPatient.demographics).length === 0) && (
                    <div className="col-span-full text-gray-500 text-center py-4">
                      No demographic information available
                    </div>
                  )}
                </div>
              </div>

              {/* Medical History Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">Medical History</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(viewingPatient.medical_history || {}).map(([key, value]) => (
                    value && (
                      <div key={key} className="bg-gray-50 p-3 rounded">
                        <label className="text-sm font-medium text-gray-700 capitalize">
                          {key.replace(/_/g, ' ')}
                        </label>
                        <p className="text-sm text-gray-900 mt-1">
                          {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
                        </p>
                      </div>
                    )
                  ))}
                  {(!viewingPatient.medical_history || Object.keys(viewingPatient.medical_history).length === 0) && (
                    <div className="col-span-full text-gray-500 text-center py-4">
                      No medical history information available
                    </div>
                  )}
                </div>
              </div>

              {/* Therapy Details Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">Therapy Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(viewingPatient.therapy_details || {}).map(([key, value]) => (
                    value && (
                      <div key={key} className="bg-gray-50 p-3 rounded">
                        <label className="text-sm font-medium text-gray-700 capitalize">
                          {key.replace(/_/g, ' ')}
                        </label>
                        <p className="text-sm text-gray-900 mt-1">
                          {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
                        </p>
                      </div>
                    )
                  ))}
                  {(!viewingPatient.therapy_details || Object.keys(viewingPatient.therapy_details).length === 0) && (
                    <div className="col-span-full text-gray-500 text-center py-4">
                      No therapy details available
                    </div>
                  )}
                </div>
              </div>

              {/* Outcomes Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">Outcomes</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(viewingPatient.outcomes || {}).map(([key, value]) => (
                    value && (
                      <div key={key} className="bg-gray-50 p-3 rounded">
                        <label className="text-sm font-medium text-gray-700 capitalize">
                          {key.replace(/_/g, ' ')}
                        </label>
                        <p className="text-sm text-gray-900 mt-1">
                          {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
                        </p>
                      </div>
                    )
                  ))}
                  {(!viewingPatient.outcomes || Object.keys(viewingPatient.outcomes).length === 0) && (
                    <div className="col-span-full text-gray-500 text-center py-4">
                      No outcome information available
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setViewingPatient(null);
                }}
                className="btn-primary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Entry Modal */}
      {showAIEntry && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">🤖 AI-Powered Patient Entry</h2>
                <button
                  onClick={resetAIEntry}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {!aiPreviewMode ? (
                <>
                  {/* AI Input Section */}
                  <div className="space-y-4">
                    <div className="bg-sky-50 p-4 rounded-lg">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          <svg className="h-6 w-6 text-sky-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-lg font-medium text-sky-900">AI-Powered Data Extraction</h3>
                          <p className="text-sm text-sky-700 mt-1">
                            Describe the patient in natural language by typing or speaking. Our AI will extract structured data automatically.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Audio Recording Controls */}
                    {audioSupported && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              <svg className="h-6 w-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div>
                              <h4 className="text-sm font-medium text-green-900">🎤 Smart Voice Input</h4>
                              <p className="text-xs text-green-700">
                                {('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) 
                                  ? "Real-time speech recognition - words appear as you speak"
                                  : "Voice recording with AI transcription"}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-3">
                            {isRecording && (
                              <div className="flex items-center space-x-2 text-red-600">
                                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                                <span className="text-sm font-medium">
                                  🔴 {formatRecordingTime(recordingTime)}
                                </span>
                              </div>
                            )}
                            
                            {transcribing && (
                              <div className="flex items-center space-x-2 text-sky-600">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-sky-600"></div>
                                <span className="text-sm">Transcribing...</span>
                              </div>
                            )}
                            
                            {!isRecording && !transcribing && (
                              <button
                                onClick={startRecording}
                                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors flex items-center space-x-2"
                              >
                                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
                                </svg>
                                <span>Start Speaking</span>
                              </button>
                            )}
                            
                            {isRecording && (
                              <button
                                onClick={stopRecording}
                                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors flex items-center space-x-2"
                              >
                                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 2a8 8 0 100 16 8 8 0 000-16zM8 7a1 1 0 000 2h4a1 1 0 100-2H8z" clipRule="evenodd" />
                                </svg>
                                <span>Stop Speaking</span>
                              </button>
                            )}
                          </div>
                        </div>
                        
                        {(isRecording || transcribing) && (
                          <div className="mt-3 p-3 bg-white rounded border border-green-300">
                            <div className="text-xs text-gray-600">
                              {isRecording && !transcribing && "🎤 Listening... Speak clearly about patient demographics, medical history, treatments, and outcomes."}
                              {transcribing && "🤖 Converting speech to text using AI transcription..."}
                              {isRecording && ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) && (
                                <div className="mt-1 text-green-600">
                                  ✨ Real-time transcription - your words appear instantly in the text area below
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div>
                      <label className="form-label">Patient Description (Type or Speak)</label>
                      <textarea
                        className="form-input"
                        rows={8}
                        value={aiInputText}
                        onChange={(e) => setAiInputText(e.target.value)}
                        placeholder={generateDynamicExample()}
                        disabled={isRecording || transcribing}
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        The example above is customized for your "{templateData?.name || 'current'}" template. 
                        Include similar information about demographics, medical history, treatments, and outcomes.
                        {audioSupported && " You can also use the microphone button above to speak your description."}
                      </p>
                      
                      {/* Template Field Reference */}
                      {templateData && templateData.template_data && templateData.template_data.sections && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg border">
                          <h5 className="text-xs font-medium text-gray-700 mb-2">
                            📋 Template Fields Reference ({templateData.name} - {templateData.specialty})
                          </h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                            {templateData.template_data.sections.map((section, index) => (
                              <div key={index} className="bg-white p-2 rounded border">
                                <div className="font-medium text-gray-800 mb-1">{section.title}</div>
                                <div className="text-gray-600 space-y-0.5">
                                  {section.fields?.slice(0, 4).map((field, fieldIndex) => (
                                    <div key={fieldIndex} className="flex items-center">
                                      <div className="w-1 h-1 bg-gray-400 rounded-full mr-2"></div>
                                      <span>{field.label}</span>
                                      {field.required && <span className="text-red-500 ml-1">*</span>}
                                    </div>
                                  ))}
                                  {section.fields && section.fields.length > 4 && (
                                    <div className="text-gray-400">
                                      ... and {section.fields.length - 4} more fields
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="text-xs text-gray-500 mt-2">
                            💡 Mention information related to these fields for best AI extraction results
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex space-x-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={resetAIEntry}
                      className="flex-1 btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleAIExtraction(false)}
                      disabled={aiExtracting || !aiInputText.trim()}
                      className="flex-1 btn-primary disabled:opacity-50"
                    >
                      {aiExtracting ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Extracting Data...
                        </div>
                      ) : (
                        'Extract Data'
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* AI Preview Section */}
                  <div className="space-y-6">
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          <svg className="h-6 w-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-lg font-medium text-green-900">Data Extracted Successfully!</h3>
                          <p className="text-sm text-green-700 mt-1">
                            Review the extracted information below and confirm to create the patient record.
                          </p>
                        </div>
                      </div>
                    </div>

                    {aiExtractedData && (
                      <div className="space-y-6">
                        {/* Patient ID */}
                        {aiExtractedData.patient_id && (
                          <div>
                            <h4 className="text-md font-medium text-gray-900 mb-2">Patient ID</h4>
                            <div className="bg-gray-50 p-3 rounded">
                              <p className="text-sm text-gray-900">{aiExtractedData.patient_id}</p>
                            </div>
                          </div>
                        )}

                        {/* Demographics */}
                        {aiExtractedData.demographics && Object.keys(aiExtractedData.demographics).length > 0 && (
                          <div>
                            <h4 className="text-md font-medium text-gray-900 mb-2">Demographics</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                              {Object.entries(aiExtractedData.demographics).map(([key, value]) => (
                                value && (
                                  <div key={key} className="bg-gray-50 p-3 rounded">
                                    <label className="text-xs font-medium text-gray-700 capitalize">
                                      {key.replace(/_/g, ' ')}
                                    </label>
                                    <p className="text-sm text-gray-900 mt-1">
                                      {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
                                    </p>
                                  </div>
                                )
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Medical History */}
                        {aiExtractedData.medical_history && Object.keys(aiExtractedData.medical_history).length > 0 && (
                          <div>
                            <h4 className="text-md font-medium text-gray-900 mb-2">Medical History</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {Object.entries(aiExtractedData.medical_history).map(([key, value]) => (
                                value && (
                                  <div key={key} className="bg-gray-50 p-3 rounded">
                                    <label className="text-xs font-medium text-gray-700 capitalize">
                                      {key.replace(/_/g, ' ')}
                                    </label>
                                    <p className="text-sm text-gray-900 mt-1">
                                      {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
                                    </p>
                                  </div>
                                )
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Therapy Details */}
                        {aiExtractedData.therapy_details && Object.keys(aiExtractedData.therapy_details).length > 0 && (
                          <div>
                            <h4 className="text-md font-medium text-gray-900 mb-2">Therapy Details</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {Object.entries(aiExtractedData.therapy_details).map(([key, value]) => (
                                value && (
                                  <div key={key} className="bg-gray-50 p-3 rounded">
                                    <label className="text-xs font-medium text-gray-700 capitalize">
                                      {key.replace(/_/g, ' ')}
                                    </label>
                                    <p className="text-sm text-gray-900 mt-1">
                                      {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
                                    </p>
                                  </div>
                                )
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Outcomes */}
                        {aiExtractedData.outcomes && Object.keys(aiExtractedData.outcomes).length > 0 && (
                          <div>
                            <h4 className="text-md font-medium text-gray-900 mb-2">Outcomes</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {Object.entries(aiExtractedData.outcomes).map(([key, value]) => (
                                value && (
                                  <div key={key} className="bg-gray-50 p-3 rounded">
                                    <label className="text-xs font-medium text-gray-700 capitalize">
                                      {key.replace(/_/g, ' ')}
                                    </label>
                                    <p className="text-sm text-gray-900 mt-1">
                                      {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
                                    </p>
                                  </div>
                                )
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex space-x-3 pt-4 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={resetAIEntry}
                        className="btn-secondary"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleAIPreviewEdit}
                        className="btn-secondary"
                      >
                        Edit Manually
                      </button>
                      <button
                        onClick={handleAIPreviewConfirm}
                        disabled={aiExtracting}
                        className="btn-primary disabled:opacity-50"
                      >
                        {aiExtracting ? (
                          <div className="flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Creating Patient...
                          </div>
                        ) : (
                          'Confirm & Create Patient'
                        )}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientDataEntry;